import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { adminClientPaymentsAPI, adminInvoicesAPI } from '../../services/api';
import { formatMoney, formatDate } from '../../utils/format';

/**
 * Apply part or all of an account credit to one of the company's open invoices.
 *
 * The amount defaults to the smaller of what is left on the credit and what is
 * owed on the invoice, because that is the answer nearly every time. The server
 * checks both limits again and posts the payment to ConnectWise; the credit is
 * only reduced once ConnectWise has accepted it.
 */
export default function ApplyCreditModal({ companyId, credit, onClose, onSuccess }) {
  const remaining = parseFloat(credit.remaining_amount) || 0;
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-credit-open-invoices', companyId],
    queryFn: () => adminInvoicesAPI.list({ companyId, status: 'open', limit: 200, sortField: 'id', sortDir: 'asc' }),
    enabled: !!companyId,
  });
  const invoices = useMemo(
    () => (data?.data?.invoices || []).filter((inv) => parseFloat(inv.balance) > 0),
    [data]
  );
  const selected = invoices.find((inv) => String(inv.id) === String(invoiceId));
  const maxAmount = selected ? Math.min(remaining, parseFloat(selected.balance)) : remaining;

  const mutation = useMutation({
    mutationFn: (body) => adminClientPaymentsAPI.applyCredit(companyId, credit.id, body),
    onSuccess: (res) => onSuccess(res?.data),
    onError: (err) => setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to apply credit'),
  });

  const chooseInvoice = (id) => {
    setInvoiceId(id);
    const inv = invoices.find((i) => String(i.id) === String(id));
    if (inv) setAmount(Math.min(remaining, parseFloat(inv.balance)).toFixed(2));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const value = parseFloat(amount);
    if (!invoiceId) return setError('Choose an invoice.');
    if (!(value > 0)) return setError('Enter an amount greater than zero.');
    if (value > maxAmount + 0.0001) {
      return setError(`The most that can be applied is ${formatMoney(maxAmount)}.`);
    }
    mutation.mutate({ invoiceId: parseInt(invoiceId, 10), amount: value, notes: notes || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Apply Credit to Invoice</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="text-sm text-neutral-600 dark:text-neutral-300">
            {credit.description || 'Credit'}: <strong>{formatMoney(remaining)}</strong> available
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Invoice
            </label>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading open invoices…
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-sm text-neutral-500">This company has no open invoices.</div>
            ) : (
              <select
                value={invoiceId}
                onChange={(e) => chooseInvoice(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              >
                <option value="">Choose an invoice</option>
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    #{inv.invoiceNumber || inv.id}
                    {inv.date ? ` · ${formatDate(inv.date)}` : ''} · {formatMoney(inv.balance)} due
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxAmount.toFixed(2)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-neutral-600 dark:text-neutral-400">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || invoices.length === 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Apply Credit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
